import { useEffect, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useAtom } from 'jotai';
import { AnalyticsTable, columnOrderAtom, getDefaultOrder, lastValidOrderAtom, searchAtom } from '../components/table';
import { useHasFeatureFlag, useMetric, useMountOnce } from '/src/hooks';
import { toTitleCase } from '/src/utils/string';
import { TimeChart } from '../components/time-chart';
import { getRowColor } from '../components/row-color';
import { humanizeDimension } from '../utils';

export function DetailView({
  analytics,
  overTimeData,
  conversionsData = [],
  overTimeLoading,
  isLoading,
  hasNextPage,
  fetchNextPage,
}) {
  const [, setSearch] = useAtom(searchAtom);
  const [, setOrder] = useAtom(columnOrderAtom);
  const [, setLastValidOrder] = useAtom(lastValidOrderAtom);
  const { view } = useParams();
  const { pathname } = useLocation();
  const trackMetric = useMetric();

  const hasOverTime = useHasFeatureFlag('analytics-over-time');

  useMountOnce(() => {
    trackMetric('feature_used', {
      feature: 'web_analytics_details',
    });
    view &&
      trackMetric('web_analytics', {
        action_type: `Details: ${humanizeDimension(view, true)}`,
      });
  }, `page:${pathname}`);

  useEffect(() => {
    // ensure we reset the order to stay in sync with the url params
    setOrder(getDefaultOrder());
    setLastValidOrder(getDefaultOrder(true).id);
    setSearch('');
    return () => {
      setOrder(getDefaultOrder());
      setLastValidOrder(getDefaultOrder(true).id);
      setSearch('');
    };
  }, [setSearch, setOrder, setLastValidOrder]);

  const data = analytics?.dimension?.data ?? [];

  const totalCount = analytics?.dimension?.totalCount ?? 0;

  const orderData = useMemo(() => {
    return overTimeData.map((d, index) => ({
      [`${d.domain ? d.domain : ``}${d.name}`]: {
        index,
        color: getRowColor(index),
      },
    }));
  }, [overTimeData]);

  const order = Object.assign({}, ...orderData);

  return (
    <div className="flex flex-col space-y-5">
      {hasOverTime ? (
        <TimeChart
          selected={view}
          title={slugToTitle(view)}
          data={overTimeData}
          conversions={conversionsData}
          isLoading={overTimeLoading}
        />
      ) : null}

      <AnalyticsTable
        selected={view}
        title={slugToTitle(view)}
        analytics={data}
        totalCount={totalCount}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        orderMeta={order}
      />
    </div>
  );
}

function slugToTitle(slug) {
  if (slug === 'os') return 'Operating Systems';

  if (slug.includes('utm')) {
    return toTitleCase(slug)
      .replace('Utm', 'UTM')
      .replace('Mediums', 'Medium')
      .replace('Sources', 'Source')
      .replace('Contents', 'Content');
  }

  return toTitleCase(slug);
}
